import type { BadgeDetail } from '~kui/types/badge';

import { DEFAULT_ANYWAY_PARTNER_BG_COLOR } from '~kui/constants/anyway';

import { useAwardWalletBadgeDetailManager } from '~/composables/Award/Wallet/useAwardWalletBadgeDetailManager';
import { useWalletBadgeViewTracking } from '~/composables/Award/Wallet/useWalletBadgeViewTracking';
import { useSpotlight } from '~/composables/unified/useSpotlight';
import { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import { collectionSpotlightTarget } from '~/utils/unified/mapSpotlight';

/**
 * One badge as its detail page draws it (cover, partner, coins, the collection that earns it and
 * the viewer's progress through it), with its view tracked and its collection's spotlight. The
 * wallet's badge page and Learn's public badge page both read it; a guest gets the badge's
 * published collection and no progress.
 */
export const useWalletBadgeDetail = ({ slug, origin = AnalyticsOrigin.Wallet }: { slug: string; origin?: AnalyticsOrigin }) => {
  const {
    badge: rawBadge,
    skills,
    coinsAvailable,
    activePrizeCampaign,
    learningAssetCollection,
    hasInProgress,
    isAllCompleted,
    badgeIssuerIconPath,
    partner,
  } = useAwardWalletBadgeDetailManager(ref({ slug }));

  const badge = computed<BadgeDetail | null>(() => {
    if (!rawBadge.value) {
      return null;
    }
    const lac = learningAssetCollection.value;
    return {
      id: rawBadge.value.id,
      name: rawBadge.value.name,
      slug: rawBadge.value.slug,
      tier: rawBadge.value.tier as 'ANYWAY' | 'JOURNEY' | 'PREMIUM',
      partner: partner.value
        ? {
            name: partner.value.name,
            iconFile: partner.value.iconFile?.url ? { url: partner.value.iconFile.url } : null,
          }
        : null,
      iconFile: rawBadge.value.iconFile ?? null,
      coverImage: rawBadge.value.coverImage ?? null,
      premiumBadgeFile: rawBadge.value.premiumBadgeFile ?? null,
      earned: !!rawBadge.value.awardedToMyselfCount,
      createdAt: rawBadge.value.createdAt ?? null,
      description: rawBadge.value.meta?.description ?? null,
      disclaimer: rawBadge.value.meta?.disclaimer ?? null,
      coinsAvailable: coinsAvailable.value ?? 0,
      skillsCount: skills.value.length,
      collectionSlug: lac?.slug ?? null,
      hasInProgress: hasInProgress.value,
      isAllCompleted: isAllCompleted.value,
      partnerIconPath: badgeIssuerIconPath.value,
      progress: lac?.learningAssets?.map(a => ({
        id: a.id,
        completed: !!a.currentUserProgress?.completedAt,
      })) ?? [],
      prize: activePrizeCampaign.value,
      partnerBgColor: partner.value?.meta?.bgColorHex ?? DEFAULT_ANYWAY_PARTNER_BG_COLOR,
    };
  });

  useWalletBadgeViewTracking({ badge, awardSlug: slug, origin });

  const { spotlight, trackLinkClick: trackSpotlightLinkClick } = useSpotlight({
    source: computed(() => {
      const lac = learningAssetCollection.value;
      return lac
        ? {
            target: collectionSpotlightTarget({
              id: lac.id,
              slug: lac.slug,
              name: lac.name,
              partnerId: partner.value?.id,
              partnerName: partner.value?.name,
            }),
            spotlight: lac.spotlight ?? null,
          }
        : null;
    }),
    origin,
  });

  return { badge, rawBadge, learningAssetCollection, spotlight, trackSpotlightLinkClick };
};
